<script setup lang="ts">
import i18n from '@/i18n';

import ActionButton from '@/components/ui/ActionButton.vue';
import SheetItineraryButton from '@/components/layout/SheetItineraryButton.vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import BookmarkButton from '@/components/ui/BookmarkButton.vue';
import BottomSheet, { type BottomSheetPosition } from '@/components/layout/BottomSheet.vue';
import InfoContainer from '@/components/ui/InfoContainer.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import ShareButton from '@/components/ui/ShareButton.vue';
import Tag from '@/components/ui/InfoTag.vue';
import Common from '@/libs/common';
import { formatAlertEffect, stopAlerts as filterStopAlerts } from '@/libs/alerts-helper';
import { useCanonicalStopRedirect } from '@/use/canonical-stop-redirect';
import { useBottomSheetAnchor } from '@/use/bottom-sheet-anchor';
import { useClock } from '@/use/clock';
import { useStableIds } from '@/use/stable-ids';
import { AppRoute } from '@/libs/routing';
import { buildStopShareUrl } from '@/libs/share';
import { getCrowdingTag, formatTags, getStopAccessibilityTag, TagValues } from '@/libs/tags-helper';

import DepartureCard from './DepartureCard.vue';
import { getLastDepartureTs, getStopEmptyState, StopEmptyState } from './empty-state';
import { stopTimesOfStops, withServedStopTimesDeparted } from './stop-times-of-stops';
import { tripOccurrence } from './trip-occurrence';
import type { RowType } from './types';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { OnlineDevice } from '@/@types/device';
import type { Vehicle } from '@/@types/vehicle';
import type { Route, Shape, Stop, StopTimeData } from '@/@types/gtfs-ressource';
import { LocationType } from '@/@types/gtfs-ressource';
import type { Alert } from '@/@types/alert';
import { useDevicesStore } from '@/stores/devices';
import { useGtfsStore } from '@/stores/gtfs';
import { MapFocusType, useAppStore } from '@/stores/app';
import { useTripUpdatesStore } from '@/stores/trip-updates';
import { useAlertsStore } from '@/stores/alerts';
import { useRailStore } from '@/stores/rail';
import type { TagElement } from '@/components/ui/InfoTag.vue';

const t = i18n.global.t;

const devicesStore = useDevicesStore();
const gtfsStore = useGtfsStore();
const appStore = useAppStore();
const tripUpdatesStore = useTripUpdatesStore();
const alertsStore = useAlertsStore();
const railStore = useRailStore();

const router = useRouter();
const route = useRoute();

const props = defineProps<{
  stopId: string;
  /** `?route=`: the line the passenger came from, the only one this stop then shows. */
  routeId?: string;
}>();

useCanonicalStopRedirect(() => props.stopId);

const shareUrl = computed<string>(() =>
  buildStopShareUrl(route.params.groupId as string, gtfsStore.getCanonicalStopId(props.stopId)),
);

// Opens on the reading snap: the detail is what the passenger came for, the map is context.
const sheetPosition = ref<BottomSheetPosition>('intermediate');

// The floating itinerary button rides on the measured sheet, not on the ratio: the
// ratio is only a floor — a tall header (long stop name, tags row) on a short
// viewport pushes the real snap higher, and the button would end up behind the sheet.
const sheetAnchor = useBottomSheetAnchor();
const isStopAlertOpen = ref<boolean>(false);

const clockNow = useClock();

const devices = computed<Array<OnlineDevice>>(() => devicesStore.onlineDevices);
const vehicles = computed<Array<Vehicle>>(() => devicesStore.vehicles);
const loading = computed<boolean>(() => appStore.isLoadingAsBoolean);
const routes = computed<{ [routeId: string]: Route }>(() => gtfsStore.gtfsData.routes);
const shapes = computed<{ [shapeId: string]: Shape }>(() => gtfsStore.gtfsData.shapes);
const stop = computed<Stop>(() => stops.value[props.stopId] || {});
const stopIds = computed<Array<string>>(() => gtfsStore.stations[props.stopId] || [props.stopId]);
const stops = computed<{ [stopId: string]: Stop }>(() => gtfsStore.gtfsData.stops);

// Reached from a line's detail, the stop is read through that line only; the
// badge in the header says so, and nothing below it widens back to the network.
const focusedRoute = computed<Route | undefined>(() =>
  props.routeId ? routes.value[props.routeId] : undefined,
);

// The map keeps to what this stop is about: the routes calling here and the
// stops they serve, not the whole network.
const stopRouteIds = computed<Array<string>>(() =>
  props.routeId ? [props.routeId] : gtfsStore.getStopsRouteIds(stopIds.value),
);

// Fallback for the shapes when no departure is left to draw them from.
const focusedRouteShapes = computed<{ [shapeId: string]: Shape }>(() => {
  const routeShapes: { [shapeId: string]: Shape } = {};
  const routeId = props.routeId;
  if (!routeId) return routeShapes;
  Object.entries(shapes.value).forEach(([shapeId, shape]) => {
    const shapeRouteIds = shape.routeIds ?? (shape.routeId ? [shape.routeId] : []);
    if (shapeRouteIds.includes(routeId)) routeShapes[shapeId] = { ...shape, routeId };
  });
  return routeShapes;
});

const shownStops = computed<{ [stopId: string]: Stop }>(() => {
  const shownStops = gtfsStore.getRoutesStops(stopRouteIds.value);
  // A stop no route calls at (or one whose routes are unknown) still shows itself.
  stopIds.value.forEach(stopId => {
    if (stops.value[stopId]) shownStops[stopId] = stops.value[stopId];
  });
  if (stops.value[props.stopId]) shownStops[props.stopId] = stops.value[props.stopId];
  return shownStops;
});

const stopAlerts = computed<Array<Alert>>(() =>
  filterStopAlerts(alertsStore.alerts, [props.stopId, ...stopIds.value]),
);

// Only the first one is expanded in place; the others are reached through the counter.
const alert = computed<Alert | undefined>(() => stopAlerts.value[0]);

const alertEffect = computed<string | undefined>(() => formatAlertEffect(alert.value));

const alertsByRouteId = computed<{ [routeId: string]: Array<Alert> }>(() => {
  const alertsByRouteId: { [routeId: string]: Array<Alert> } = {};
  alertsStore.alerts.forEach(alert => {
    alert.informed_entity?.forEach(informedEntity => {
      const routeId = informedEntity.route_id;
      if (!routeId) return;
      if (!alertsByRouteId[routeId]) alertsByRouteId[routeId] = [];
      // The store holds one entry per active window of an alert, so an alert
      // whose windows overlap is present several times. The counter shown on
      // each direction must not inflate on that.
      if (alertsByRouteId[routeId].some(({ _id }) => _id === alert._id)) return;
      alertsByRouteId[routeId].push(alert);
    });
  });
  return alertsByRouteId;
});

const rows = computed<Array<RowType>>(() => {
  const rows: { [rowId: string]: RowType } = {};
  const now = Date.now() / 1000;
  stopTimes.value
    ?.filter(st => {
      return !st.last_stop && stopIds.value.indexOf(st.stop_id) >= 0;
    })
    ?.forEach(st => {
      const { trip, tripUpdate } = tripOccurrence(
        st,
        gtfsStore.getTripsOfDay,
        tripUpdatesStore.tripUpdates ?? [],
      );
      const rowId = st.route_id + st.trip_headsign;
      const departTime = (st.estimated_departure_ts || st.departure_ts) - now;
      const realTime = !!st.estimated_departure_ts;
      const tripUpdateFormated = tripUpdate
        ? tripUpdatesStore.formatTripUpdate(tripUpdate, realTime, departTime, st)
        : undefined;

      if (!rows[rowId]) {
        rows[rowId] = {
          rowId,
          routeId: st.route_id,
          tripHeadsign: st.trip_headsign,
          stopTimes: [],
          tags: formatTags(stop.value, st, tripUpdateFormated, trip),
        };
        // Add crowding tags to previous ones
        const crowding = getCrowdingTagForStop(st);
        if (crowding) rows[rowId].tags.push(crowding);
      }

      const tripUpdateDelay =
        (tripUpdateFormated?.delayedTrip && tripUpdateFormated.delayedTrip.content) ?? 0;
      // recalculate timestamp with tripUpdateDelay if no device connected,
      const timestamp = st.estimated_departure_ts || st.departure_ts + tripUpdateDelay;

      // display waiting time if next departure is in less than 1 hour, else display departure time in HH:MM
      const time =
        departTime < 3600
          ? Common.formatSec(departTime)
          : `${t('common.at')} ${Common.timestampFormatHHMM(st.estimated_departure_ts || st.departure_ts)}`;

      // skip stopTime if theorical time + delay > now
      if (timestamp >= now) {
        rows[rowId].stopTimes.push({
          timestamp,
          time,
          realTime,
          tripUpdateFormated,
          tripId: st.trip_id,
        });
      }
    });

  const rowArray: Array<RowType> = Object.values(rows);
  rowArray.forEach(r => {
    r.stopTimes.sort((a, b) => a.timestamp - b.timestamp);
  });

  rowArray.sort((a, b) => {
    if (a.stopTimes.length && b.stopTimes.length) {
      return a.stopTimes[0].timestamp - b.stopTimes[0].timestamp;
    } else {
      return a.stopTimes.length ? -1 : 1;
    }
  });

  return rowArray;
});

// Attached to a SNCF station (itself or one of its child stops): the sheet then
// opens onto the station's departure board.
const hasTrainDepartures = computed<boolean>(() => railStore.isStopLinked(props.stopId));

// Tags of the stop itself, not of a departure: `formatTags` cannot serve here, it
// only grants PMR to a stop whose trip is accessible too.
const stopTags = computed<Array<TagElement>>(() => {
  const tag = getStopAccessibilityTag(
    stop.value,
    stopIds.value.map(stopId => stops.value[stopId]),
  );
  return tag ? [tag] : [];
});

// Kept apart from the clock: the scan of the day's trips only reruns on a vehicle
// poll, not on every tick.
const stopTimesOfDay = computed<Array<StopTimeData>>(() => {
  const allStopTimes = gtfsStore.getStopTimes(stopIds.value);
  return props.routeId ? allStopTimes.filter(st => st.route_id === props.routeId) : allStopTimes;
});

const stopTimes = computed<Array<StopTimeData>>(() =>
  stopTimesOfStops(stopTimesOfDay.value, stopIds.value, clockNow.value, devices.value),
);

const lastDepartureTs = computed<number | undefined>(() =>
  getLastDepartureTs(withServedStopTimesDeparted(stopTimesOfDay.value, devices.value)),
);

const emptyState = computed<StopEmptyState | undefined>(() =>
  getStopEmptyState(stopTimesOfDay.value, lastDepartureTs.value, clockNow.value),
);

watch(loading, () => {
  if (!loading.value) {
    onStopIdChange();
  }
});

watch(
  () => stop.value,
  () => {
    onStopIdChange();
  },
);

watch(
  shownStops,
  () => {
    gtfsStore.setShownStops(shownStops.value);
  },
  { immediate: true },
);

// stopTimes is rebuilt on every tick and vehicle poll, while the shapes and vehicles
// it calls for seldom change: pushing them only when their ids change keeps the map
// from re-tiling every shape each time.
const stopShapeIds = useStableIds(() => [...new Set(stopTimes.value.map(st => st.shape_id))]);

const stopDeviceIds = useStableIds(() => stopTimes.value.flatMap(st => (st.device_id ? [st.device_id] : [])));

watch(
  [stopShapeIds, shapes, focusedRouteShapes],
  ([shapeIds]) => {
    let shownShapes: { [shapeId: string]: Shape } = {};
    shapeIds.forEach(shapeId => {
      shownShapes[shapeId] = shapes.value[shapeId];
    });
    if (shapeIds.length === 0) {
      shownShapes = props.routeId ? focusedRouteShapes.value : shapes.value;
    }
    gtfsStore.setShownShapes(shownShapes);
  },
  { immediate: true },
);

watch(stopDeviceIds, deviceIds => appStore.setSelectedStopDevices(deviceIds), { immediate: true });

// A different stop keeps the sheet where the passenger left it, but its own
// traffic info starts collapsed.
watch(
  () => props.stopId,
  () => {
    isStopAlertOpen.value = false;
  },
);

onMounted(() => {
  onStopIdChange();
  devicesStore.loadVehicles();
});

function onStopIdChange() {
  appStore.setSelectedStopId(props.stopId);
  if (!stop.value.stop_lat) return;
  appStore.setMapFocus({
    type: stop.value.location_type === LocationType.STATION ? MapFocusType.STATION : MapFocusType.STOP,
    elem: stop.value,
  });
}

function onSheetHeight(height: number, dragging: boolean) {
  sheetAnchor.onHeight(height, dragging);
  appStore.setMapBottomInset(height, dragging);
}

function goToItinerary() {
  router.push({ name: AppRoute.ITINERARY, query: { to: props.stopId } });
}

function goToTimetable() {
  router.push({ name: AppRoute.TIMETABLE, params: { stopId: props.stopId } });
}

function goToTrainDepartures() {
  router.push({ name: AppRoute.TRAIN_DEPARTURES, params: { stopId: props.stopId } });
}

/**
 * Get the crowding tag for a stop time based on the device and vehicle data.
 * Only the trip the vehicle is actually operating gets a crowding tag — upcoming
 * trips of the same block (sequential service) must not inherit the live load.
 * @returns The crowding tag or null if no vehicle or device can be determined.
 */
function getCrowdingTagForStop(st: StopTimeData): TagElement | null {
  if (devices.value?.length === 0 || vehicles.value?.length === 0 || !st.device_id) return null;

  const device = devices.value.find(d => d.device_id === st.device_id);
  const vehicle = vehicles.value.find(v => v.id === device?.last_vehicle_associated);

  return getCrowdingTag(device, vehicle, st.trip_id);
}
</script>

<template>
  <div class="stop-panel">
    <SheetItineraryButton :anchor="sheetAnchor" :label="$t('stop.itineraryTo')" @click="goToItinerary" />

    <BottomSheet
      v-model:position="sheetPosition"
      @update:height="onSheetHeight"
      @update:intermediateHeight="sheetAnchor.onIntermediateHeight"
    >
      <template #header>
        <div class="stop-panel__header">
          <div class="stop-panel__titles">
            <div class="stop-panel__name-line">
              <RouteBadge v-if="focusedRoute" class="stop-panel__route-badge" :route="focusedRoute" />
              <div class="stop-panel__name">{{ stop.stop_name }}</div>
            </div>
            <span class="stop-panel__bookmark">
              <ShareButton :title="stop.stop_name" :url="shareUrl" />
              <BookmarkButton :element="stop" />
            </span>
          </div>
          <div v-if="stopTags.length > 0" class="stop-panel__tags">
            <Tag v-for="tag in stopTags" :key="tag.tagType" :tag-type="tag.tagType" :tag-data="tag.tagData" />
          </div>
        </div>
      </template>

      <button v-if="hasTrainDepartures" class="stop-panel__trains" type="button" @click="goToTrainDepartures">
        <BaseIcon name="train-front" class="stop-panel__trains-icon" />
        <span class="stop-panel__trains-label">{{ $t('stop.showTrainSchedules') }}</span>
        <BaseIcon name="chevron-right" class="stop-panel__trains-icon" />
      </button>

      <InfoContainer
        v-if="alert"
        v-model:open="isStopAlertOpen"
        :tag-label="alertEffect"
        :title="alert.header_text"
      >
        <div v-if="alert.description_text" v-safe-html="alert.description_text" />
      </InfoContainer>

      <router-link
        v-if="alert && stopAlerts.length > 1"
        class="stop-panel__stop-alerts"
        :to="{ name: AppRoute.PASSENGER_MESSAGE, params: { alertId: alert._id } }"
      >
        <!-- The first one is already expanded above: only count the others -->
        <Tag :tag-type="TagValues.TRAFFIC_INFO_COUNT" :tag-data="stopAlerts.length - 1" />
      </router-link>

      <!-- One card per route and direction, soonest departure first -->
      <div class="stop-panel__departures">
        <DepartureCard
          v-for="row in rows"
          :key="row.rowId"
          :alert-count="(alertsByRouteId[row.routeId] ?? []).length"
          :loading="loading"
          :route="routes[row.routeId]"
          :row="row"
          :stop-id="stopId"
        />

        <div v-if="rows.length === 0 && emptyState">
          <div class="stop-panel__empty">
            <BaseIcon name="moon" class="stop-panel__empty-icon" />
            <span class="stop-panel__empty-text">{{ $t(`common.${emptyState}`) }}</span>
          </div>
        </div>

        <ActionButton
          class="stop-panel__timetables-button"
          :label="$t('stop.seeAllTimetables')"
          size="large"
          trailing-icon="chevron-right"
          type="ghost"
          @click="goToTimetable"
        />
      </div>
    </BottomSheet>
  </div>
</template>

<style lang="scss">
.stop-panel {
  @include sheet-host;

  &__header {
    border-bottom: 1px solid $color-border-medium;
    display: flex;
    flex-direction: column;
    gap: $spacing-3;
    padding: $spacing-4;
  }

  &__titles {
    align-items: center;
    display: flex;
    gap: $spacing-4;
    min-width: 0;
  }

  &__name-line {
    align-items: center;
    display: flex;
    flex: 1;
    gap: $spacing-2;
    min-width: 0;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: $spacing-2;
  }

  // Optically centred on the name's first line, so a name wrapping onto 2 lines
  // does not drag the badge down to the middle of the block.
  &__route-badge {
    align-self: flex-start;
    flex-shrink: 0;
    margin-top: 4px;
  }

  &__name {
    flex: 1;
    color: $color-neutral-highest;
    display: -webkit-box;
    font: 600 24px / 32px $font-poppins;
    letter-spacing: $letter-spacing-tight;
    // A very long stop_name wraps onto 2 lines, then clips
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
  }

  &__bookmark {
    @include icon-pill;
  }

  &__trains {
    align-items: center;
    background-color: $color-white;
    border: none;
    border-bottom: 1px solid $color-border-medium;
    color: $color-neutral-highest;
    cursor: pointer;
    display: flex;
    gap: $spacing-2;
    padding: $spacing-4;
    text-align: left;
    width: 100%;

    &:active {
      background-color: $color-neutral-lower;
    }
  }

  &__trains-icon {
    font-size: 20px;
  }

  &__trains-label {
    flex: 1;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    min-width: 0;
  }

  &__stop-alerts {
    display: block;
    padding: $spacing-3 $spacing-4 0;
  }

  &__departures {
    display: flex;
    flex-direction: column;
    gap: $spacing-3;
    padding: $spacing-5 $spacing-4;
  }

  &__empty {
    align-items: center;
    display: flex;
    gap: $spacing-1;
    padding: $spacing-4 0;
  }

  &__empty-icon {
    color: $color-secondary;
    font-size: 20px;
  }

  &__empty-text {
    color: $color-neutral-higher;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
  }

  &__timetables-button {
    width: 100%;
  }

  // The cards are white: the scroller behind them carries the grey ground, so it
  // still covers the sheet when the list is shorter than the content box.
  .bottom-sheet__content {
    background-color: $color-neutral-lower;
  }
}
</style>
